import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useContentVersions,
  useHideContentVersion,
  useUpdateContentVersionLabel,
} from '@opallabs/api'
import {
  Button,
  Drawer,
  DrawerOrigin,
  DrawerSize,
  Modal,
  Pill,
} from '@opallabs/lapidary'
import { hasHiddenSections } from './deriveTranslatorInputs'
import { filterVersions } from './isMilestone'
import * as styles from './styles'
import type { VersionFilter } from './types'
import { VersionPreview } from './VersionPreview'
import { VersionsRail } from './VersionsRail'

type ContentVersionHistoryDrawerProps = {
  isOpen: boolean
  onClose: () => void
  /** The content UUID whose versions are shown. */
  contentId: string
  /**
   * When set, the drawer opens preselected to the version produced by this edit
   * Activity (falling back to the newest version if none matches). Used to
   * deep-link from the activity feed's per-edit "view edits" action.
   */
  initialActivityId?: number | null
  /**
   * When set, the drawer opens preselected to this version, taking precedence over
   * `initialActivityId`. Feedback activities name their version directly.
   */
  initialVersionId?: string | null
  /**
   * When set, the preselected version opens with this pin's popover showing. Used to
   * deep-link from the activity feed's "view feedback" action.
   */
  initialAnnotationId?: string | null
  /**
   * Incremented by the host every time the content saves — the drawer's react-query
   * cache is invisible to the Backbone composer that owns the save. `0` means nothing
   * has saved since the drawer mounted.
   */
  refreshToken?: number
  /**
   * Whether the viewer may edit this content. Leaving and resolving feedback needs
   * only view access, but labelling and deleting versions are edit-gated server-side
   * — so those affordances are hidden rather than left to 403. Defaults to read-only:
   * a host that cannot tell must not offer them.
   */
  canEdit?: boolean
}

/**
 * Versions are captured in a background job off the save pipeline, so a refetch
 * fired by the save (or by an open moments later) can beat the version it is
 * looking for into existence. Long enough for the job to have run.
 */
const CAPTURE_RETRY_DELAY_MS = 3_500

const formatHeaderDate = (isoDate: string): string =>
  new Date(isoDate).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

/**
 * Full-takeover drawer for a content's version history: a live read-only preview in
 * the center and a rail of versions on the right. Versions arrive newest first; the
 * newest is the current content.
 */
export const ContentVersionHistoryDrawer: React.VFC<
  ContentVersionHistoryDrawerProps
> = ({
  isOpen,
  onClose,
  contentId,
  initialActivityId = null,
  initialVersionId = null,
  initialAnnotationId = null,
  refreshToken = 0,
  canEdit = false,
}) => {
  const {
    data: versions,
    refetch,
    isFetching,
  } = useContentVersions(contentId, isOpen)

  // The drawer stays mounted while closed and nothing bridges the Backbone save
  // to query invalidation, so each open — and each save the host reports —
  // refetches. react-query dedupes the overlapping fetch on first open.
  useEffect(() => {
    if (isOpen) refetch()
  }, [isOpen, refreshToken, refetch])
  const { mutate: updateLabel } = useUpdateContentVersionLabel(contentId)
  const { mutate: hideVersion } = useHideContentVersion(contentId)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filter, setFilter] = useState<VersionFilter>('all')
  const [compareMode, setCompareMode] = useState(false)
  // Read by the Escape handler below, which Radix calls from a listener it
  // registered before this drawer had any comment mode to report. A ref keeps
  // that handler reading the live value without re-rendering the drawer.
  const commentModeRef = useRef(false)
  const [comparedIds, setComparedIds] = useState<string[]>([])

  const orderedVersions = useMemo(() => versions ?? [], [versions])

  // Read by the retry timer below, which must see the current list without
  // re-running (and rescheduling itself) every time the list changes.
  const versionCount = useRef(orderedVersions.length)
  useEffect(() => {
    versionCount.current = orderedVersions.length
  }, [orderedVersions])

  // Capture runs in a background job, so the refetch above can come back with
  // the same list the user was already looking at. Give each save one late
  // retry, and skip it if the list grew in the meantime — that fetch found the
  // version we were waiting for. Deliberately not a polling loop: reopening the
  // drawer is the user's own retry.
  const retriedToken = useRef(0)
  useEffect(() => {
    if (!isOpen || refreshToken === retriedToken.current) return
    retriedToken.current = refreshToken
    const countBeforeRetry = versionCount.current
    const timer = setTimeout(() => {
      if (versionCount.current === countBeforeRetry) refetch()
    }, CAPTURE_RETRY_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isOpen, refreshToken, refetch])

  const hasDeepLinkTarget =
    initialVersionId != null || initialActivityId != null
  const deepLinkedVersion = useMemo(() => {
    if (initialVersionId != null)
      return orderedVersions.find((v) => v.id === initialVersionId) ?? null
    if (initialActivityId != null)
      return (
        orderedVersions.find((v) => v.activityId === initialActivityId) ?? null
      )
    return null
  }, [orderedVersions, initialActivityId, initialVersionId])

  // Preselect the deep-linked version once per open, and only once it is
  // actually in the list. Latching on the first list that arrived instead would
  // drop the link twice over: the drawer stays mounted while closed, so the
  // first list a reopen sees is the stale cached one, and a version captured by
  // the save that opened the drawer only lands with the retry above.
  const [hasResolvedDeepLink, setHasResolvedDeepLink] = useState(false)
  useEffect(() => {
    if (!isOpen) {
      setHasResolvedDeepLink(false)
      return
    }
    if (hasResolvedDeepLink || !deepLinkedVersion) return
    setHasResolvedDeepLink(true)
    setSelectedId(deepLinkedVersion.id)
  }, [isOpen, hasResolvedDeepLink, deepLinkedVersion])

  // A link to a version that isn't in the list: it has been deleted, or hidden,
  // since the link was made. Falling back to the newest version silently shows
  // the reader something that isn't what they followed the link for. Read once
  // the fetch settles — mid-refetch the list is the stale one from last open.
  const isDeepLinkedVersionMissing =
    hasDeepLinkTarget &&
    !hasResolvedDeepLink &&
    !isFetching &&
    orderedVersions.length > 0

  // Choosing a version by hand settles the deep link for good. Resolving a pin
  // invalidates the version list, and without this the effect above would drag
  // the reader back to the deep-linked version on the fetch that follows.
  const handleSelect = useCallback((id: string | null) => {
    setHasResolvedDeepLink(true)
    setSelectedId(id)
  }, [])

  // The drawer slides in over 500ms; a popover positioned — or scrolled to —
  // against the still-translating panel lands somewhere else entirely, so the
  // deep-linked pin waits for the entry animation to finish. Browsers that run
  // no animation at all (reduced motion, jsdom) report none to wait for.
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [hasFinishedEntering, setHasFinishedEntering] = useState(false)
  useEffect(() => {
    if (!isOpen) {
      setHasFinishedEntering(false)
      return
    }
    const running = contentRef.current?.getAnimations?.() ?? []
    if (running.length === 0) setHasFinishedEntering(true)
  }, [isOpen])

  const activeIndex = useMemo(() => {
    const index = orderedVersions.findIndex((v) => v.id === selectedId)
    return index === -1 ? 0 : index
  }, [orderedVersions, selectedId])
  const activeVersion = orderedVersions[activeIndex] ?? null

  // Two versions ordered oldest-first: the older edit is the "Before", the newer the
  // "After". `orderedVersions` is newest-first, so reverse the matched pair.
  const comparedVersions = useMemo(() => {
    if (comparedIds.length !== 2) return []
    return orderedVersions.filter((v) => comparedIds.includes(v.id)).reverse()
  }, [comparedIds, orderedVersions])

  // Stepping walks the versions visible in the rail, so the Milestones filter and
  // the Newer/Older buttons never disagree.
  const steppableVersions = useMemo(
    () => filterVersions(orderedVersions, filter),
    [filter, orderedVersions]
  )
  const stepIndex = useMemo(() => {
    const index = steppableVersions.findIndex((v) => v.id === activeVersion?.id)
    return index === -1 ? 0 : index
  }, [steppableVersions, activeVersion])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose()
        setSelectedId(null)
        setFilter('all')
        setCompareMode(false)
        setComparedIds([])
      }
    },
    [onClose]
  )

  const handleLabelCommit = useCallback(
    (id: string, label: string | null) => updateLabel({ id, label }),
    [updateLabel]
  )

  // A filter that leaves fewer than two versions on screen leaves nothing to
  // compare — and a checked version the rail no longer renders can't be
  // unchecked, so compare would be stuck.
  const handleFilterChange = useCallback(
    (next: VersionFilter) => {
      setFilter(next)
      if (filterVersions(orderedVersions, next).length < 2) {
        setCompareMode(false)
        setComparedIds([])
      }
    },
    [orderedVersions]
  )

  const handleToggleCompareMode = useCallback(() => {
    if (compareMode) {
      setCompareMode(false)
      setComparedIds([])
    } else {
      setCompareMode(true)
      // Seed with the version in view so it starts checked — but only when the
      // rail is showing it. A checked row that isn't rendered can't be unchecked.
      const seed = steppableVersions.some((v) => v.id === activeVersion?.id)
        ? activeVersion
        : null
      setComparedIds(seed ? [seed.id] : [])
    }
  }, [compareMode, activeVersion, steppableVersions])

  const handleToggleCompared = useCallback((id: string) => {
    setComparedIds((prev) => {
      if (prev.includes(id)) return prev.filter((existing) => existing !== id)
      // Keep at most two; checking a third drops the oldest-checked (FIFO).
      return [...prev, id].slice(-2)
    })
  }, [])

  // Presented to the user as deletion; the server soft-hides so history and
  // future annotation anchors survive.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const handleConfirmDelete = useCallback(() => {
    const id = pendingDeleteId
    if (!id) return
    hideVersion({ id })
    if (selectedId === id) setSelectedId(null)
    setComparedIds((prev) => prev.filter((existing) => existing !== id))
    setPendingDeleteId(null)
  }, [hideVersion, pendingDeleteId, selectedId])

  const canStepNewer = stepIndex > 0
  const canStepOlder = stepIndex < steppableVersions.length - 1

  // Sectioned content previews every section at once (see VersionPreview), which
  // is a wider layout than a single card; the column widens to hold it. Compare
  // mode always previews the collapsed card.
  const isExpandedPreview =
    !compareMode &&
    activeVersion != null &&
    hasHiddenSections(activeVersion.blob)

  return (
    <Drawer.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Drawer.Content
        ref={contentRef}
        drawerOrigin={DrawerOrigin.Bottom}
        drawerSize={DrawerSize.Full}
        css={styles.drawer}
        className="content-version-history-drawer"
        // Only the panel's own slide counts; animations inside it also bubble.
        onAnimationEnd={(event) => {
          if (event.target === event.currentTarget) setHasFinishedEntering(true)
        }}
        // Contain Escape so closing the drawer doesn't also close the composer
        // hosting it.
        //
        // Comment mode is a layer inside the drawer, and Escape steps out of the
        // innermost layer first. Radix registers its own document listener in the
        // capture phase when this content mounts, so a listener added later (when
        // comment mode turns on) cannot stop it — but Radix skips dismissing a
        // press it sees already default-prevented, which is what this does. An
        // open popover is a higher layer and never reaches here at all.
        onEscapeKeyDown={(event) => {
          if (commentModeRef.current) event.preventDefault()
          event.stopPropagation()
        }}
      >
        <section css={styles.preview(isExpandedPreview)}>
          {compareMode ? (
            comparedVersions.length === 2 ? (
              <div css={styles.compareGrid}>
                {comparedVersions.map((version, index) => (
                  <div key={version.id} css={styles.comparePane}>
                    <div css={styles.comparePaneHeader}>
                      <h2 css={styles.previewHeaderTitle}>
                        {index === 0 ? 'Before Edit' : 'After Edit'}
                      </h2>
                      <span css={styles.previewHeaderDate}>
                        {formatHeaderDate(version.createdAt)}
                      </span>
                    </div>
                    {/* Keyed per version: ConnectedPost is a legacy connected
                        component that does not react to prop swaps after mount. */}
                    <VersionPreview
                      key={version.id}
                      blob={version.blob}
                      fitToWidth
                      snapshotUrl={version.snapshotUrl}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p css={styles.previewNotice}>Select two versions to compare</p>
            )
          ) : isDeepLinkedVersionMissing ? (
            <p css={styles.previewNotice}>
              This version was deleted. Pick another from the list to keep
              looking.
            </p>
          ) : (
            <>
              <div css={styles.previewHeader}>
                <div>
                  <h2 css={styles.previewHeaderTitle}>
                    {activeVersion?.blob.post.name || 'Version'}
                  </h2>
                  <span css={styles.previewHeaderSubtitle}>
                    {activeVersion ? (
                      // Versions arrive newest first, but they are numbered in
                      // the order they were made: the newest is version N.
                      `Version ${orderedVersions.length - activeIndex} of ${orderedVersions.length}`
                    ) : (
                      <Fragment>&nbsp;</Fragment>
                    )}
                  </span>
                </div>
                <div css={styles.steppingControls}>
                  {activeIndex === 0 && activeVersion && (
                    <Pill text="Current" color="green" size="small" solid />
                  )}
                  <Button
                    name="Newer version"
                    buttonText="Newer"
                    variant="tertiary"
                    disabled={!canStepNewer}
                    onClick={() =>
                      handleSelect(steppableVersions[stepIndex - 1]?.id ?? null)
                    }
                  />
                  <Button
                    name="Older version"
                    buttonText="Older"
                    variant="tertiary"
                    disabled={!canStepOlder}
                    onClick={() =>
                      handleSelect(steppableVersions[stepIndex + 1]?.id ?? null)
                    }
                  />
                </div>
              </div>
              {/* Keyed by version: ConnectedPost is a legacy connected component that
                  does not react to prop swaps after mount, so each version remounts. */}
              {activeVersion && (
                <VersionPreview
                  key={activeVersion.id}
                  blob={activeVersion.blob}
                  version={activeVersion}
                  isCurrent={activeIndex === 0}
                  initialAnnotationId={
                    hasFinishedEntering ? initialAnnotationId : null
                  }
                  canEdit={canEdit}
                  snapshotUrl={activeVersion.snapshotUrl}
                  onCommentModeChange={(active) => {
                    commentModeRef.current = active
                  }}
                  onLabelCommit={(label) =>
                    handleLabelCommit(activeVersion.id, label)
                  }
                />
              )}
            </>
          )}
        </section>
        <section css={styles.rail}>
          <Drawer.Header>
            <Drawer.Title>Version history</Drawer.Title>
            <Drawer.Description>
              Review and label past versions of this content.
            </Drawer.Description>
          </Drawer.Header>
          <Drawer.Close />
          <VersionsRail
            versions={orderedVersions}
            activeId={activeVersion?.id ?? null}
            filter={filter}
            compareMode={compareMode}
            comparedIds={comparedIds}
            canEdit={canEdit}
            onFilterChange={handleFilterChange}
            onSelect={handleSelect}
            onToggleCompareMode={handleToggleCompareMode}
            onToggleCompared={handleToggleCompared}
            onHide={setPendingDeleteId}
          />
        </section>
        <Modal.Root open={pendingDeleteId !== null}>
          <Modal.Content
            css={styles.deleteModal}
            overlayStyles={styles.deleteModalOverlay}
          >
            <Modal.Title>Delete this version?</Modal.Title>
            <Modal.Body>
              <Modal.Description>
                It will be removed from the version history. This can&rsquo;t be
                undone.
              </Modal.Description>
            </Modal.Body>
            <Modal.Footer>
              <Button
                buttonText="Cancel"
                name="Cancel"
                onClick={() => setPendingDeleteId(null)}
                variant="ghost"
              />
              <Button
                buttonText="Delete"
                name="Delete"
                onClick={handleConfirmDelete}
                variant="primaryDestructive"
              />
            </Modal.Footer>
          </Modal.Content>
        </Modal.Root>
      </Drawer.Content>
    </Drawer.Root>
  )
}
